import { useRef, useEffect } from 'react';
import * as Plot from '@observablehq/plot';
import { useI18n } from 'next-localization';
import styles from 'components/Pages/FundsCenter/FundDetails/FundDetailsPerformanceTabCumulativeSection/Chart.module.scss';
import { PremiumDiscountChart } from 'model/ApiGateway';

const FundDetailsPremiumDiscountBarChart = ({ data }: PremiumDiscountChart[]) => {
  const chartWrapper = useRef(null);
  const { t } = useI18n();

  useEffect(() => {
    if (!data) return;

    const startValues = data.map((d) => d.premiumDiscountStartValueWithoutLocalization);

    const minValue = Math.min(...startValues);
    const maxValue = Math.max(...startValues) + 0.5;

    const plot = Plot.plot({
      x: {
        tickFormat: (d) => (d === 0 ? t('fundDetailsPremiumDiscountNav') : `${d}%`),
        domain: [Math.min(minValue, -3), Math.max(maxValue, 3)],
        tickSpacing: 60,
      },
      y: {
        label: t('days'),
        grid: true,
        nice: true,
      },
      marginTop: 40,
      width: chartWrapper.current?.offsetWidth,
      height: chartWrapper.current?.offsetHeight,
      marks: [
        Plot.rectY(data, {
          x1: 'premiumDiscountStartValueWithoutLocalization',
          x2: 'premiumDiscountEndValueWithoutLocalization',
          y: 'numberOfDays',
          fill: (d) => (d.premiumDiscountEndValueWithoutLocalization > 0 ? '#00205B' : '#BBBCBC'),
          insetLeft: 1,
          insetRight: 1,
        }),

        Plot.ruleX([0], {
          strokeWidth: 2,
          strokeDasharray: '8 4',
        }),

        Plot.ruleY([0]),

        Plot.tip(
          data,
          Plot.pointerX({
            x1: 'premiumDiscountStartValueWithoutLocalization',
            x2: 'premiumDiscountEndValueWithoutLocalization',
            y: 'numberOfDays',
            filter: (d) => d.numberOfDays > 0,
            title: (d) =>
              `${t('days')}: ${d.numberOfDays}\n${t('fundDetailsPremiumDiscountBarChartTooltip')
                .replace('{token.startValue}', d.premiumDiscountStartValue)
                .replace('{token.endValue}', d.premiumDiscountEndValue)}`,
            strokeWidth: 0,
            textPadding: 12,
            fontSize: 12,
            lineHeight: 1.5,
            maxRadius: 999,
          })
        ),
      ],
    });

    chartWrapper.current.append(plot);

    return () => plot.remove();
  }, [data, t]);
  return (
    <div className="position-relative mt-4">
      <div ref={chartWrapper} className={styles['chart-wrapper']} />
    </div>
  );
};

export default FundDetailsPremiumDiscountBarChart;
